import { BlockType, PermissionRole } from '@next-space/fe-api-idl';
import isHotkey from 'is-hotkey';
import { useCallback } from 'react';
import { IS_WINDOWS } from 'src/common/utils/environment';
import { getPermissions } from 'src/hooks/share/use-permissions';
import { useTransaction } from 'src/hooks/use-transaction';
import { convertBlock } from 'src/redux/managers/block/convert';
import { useCloseBlockMenuList } from 'src/redux/managers/ui';
import { cache } from 'src/redux/store';
import { isCollection } from 'src/utils/block-type-utils';
import { parseEditorKeyToBlockId } from 'src/utils/editor-key-to-id';
import { selectedBlocksToIds } from 'src/utils/select-block-util';

const optORshift = IS_WINDOWS ? 'shift' : 'opt';
const convertHotKeys = Array.from(Array(10))
  .map((_, i) => `mod+${optORshift}+${i}`)
  // ctrl+shift+0 转换文本快捷键失效
  .concat('mod+opt+0')
  .concat(`mod+${optORshift}+=`) // =号
  .concat(`mod+${optORshift}+-`); // -号

const TYPE = {
  Digit0: { type: BlockType.TEXTAREA },
  Numpad0: { type: BlockType.TEXTAREA },
  Digit1: { type: BlockType.HEADER, data: { level: 1 } },
  Numpad1: { type: BlockType.HEADER, data: { level: 1 } },
  Digit2: { type: BlockType.HEADER, data: { level: 2 } },
  Numpad2: { type: BlockType.HEADER, data: { level: 2 } },
  Digit3: { type: BlockType.HEADER, data: { level: 3 } },
  Numpad3: { type: BlockType.HEADER, data: { level: 3 } },
  Digit4: { type: BlockType.HEADER, data: { level: 4 } },
  Numpad4: { type: BlockType.HEADER, data: { level: 4 } },
  Digit5: { type: BlockType.TODO },
  Numpad5: { type: BlockType.TODO },
  Digit6: { type: BlockType.LIST },
  Numpad6: { type: BlockType.LIST },
  Digit7: { type: BlockType.ORDER_LIST },
  Numpad7: { type: BlockType.ORDER_LIST },
  Digit8: { type: BlockType.FOLD_LIST },
  Numpad8: { type: BlockType.FOLD_LIST },
  Digit9: { type: BlockType.PAGE },
  Numpad9: { type: BlockType.PAGE },
  Minus: { type: BlockType.CODE },
  NumpadSubtract: { type: BlockType.CODE },
  Equal: { type: BlockType.EQUATION },
  NumpadEqual: { type: BlockType.EQUATION },
};

export const useConvertKey = () => {
  const transaction = useTransaction();
  const closeBlockMenuList = useCloseBlockMenuList();

  const convertKey = (event: KeyboardEvent, pageId: string) => {
    if (
      !(
        isHotkey(convertHotKeys, { byKey: true })(event) ||
        isHotkey(convertHotKeys, { byKey: false })(event)
      )
    ) {
      return;
    }

    const { role } = getPermissions(pageId);

    if (role === PermissionRole.READER || role === PermissionRole.NONE) return;

    event.preventDefault();

    const editorKey = document.activeElement?.getAttribute('data-editor');
    const uuid = parseEditorKeyToBlockId(editorKey);

    if (uuid === pageId) return;

    const { selectedBlocks } = cache.ui;
    if (!uuid && selectedBlocks.length === 0) return;

    const blocks = selectedBlocks.length
      ? selectedBlocksToIds(cache.ui.selectedBlocks)
      : uuid
      ? [uuid]
      : [];

    // 选中的块中有 多维表 或 文件夹
    if (
      blocks.some((id) => {
        const b = cache.blocks[id];
        const type = b?.type;
        return isCollection(type) || type === BlockType.FOLDER || type === BlockType.FILE;
      })
    ) {
      return;
    }
    transaction(() => {
      convertBlock(blocks, (TYPE as any)[event.code]);
    });

    closeBlockMenuList();
  };

  return useCallback(convertKey, [closeBlockMenuList, transaction]);
};
